import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

/**
 * Organization Settings - Org configuration
 *
 * Features:
 * - Org name and logo
 * - Default meeting settings
 * - Guest permissions
 * - Roles & permissions info
 */

export default function OrgSettings({ organization, isOwner }) {
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    name: organization?.name || '',
    defaultWaitingRoom: organization?.settings?.defaultWaitingRoom || false,
    defaultSaveChatHistory: organization?.settings?.defaultSaveChatHistory || true,
    allowGuestRecordings: organization?.settings?.allowGuestRecordings || false,
    requireApprovalForGuests: organization?.settings?.requireApprovalForGuests || true,
  });

  const handleSave = async () => {
    try {
      setSaving(true);
      const orgRef = doc(db, 'organizations', organization.id);
      await updateDoc(orgRef, {
        name: settings.name,
        settings: {
          defaultWaitingRoom: settings.defaultWaitingRoom,
          defaultSaveChatHistory: settings.defaultSaveChatHistory,
          allowGuestRecordings: settings.allowGuestRecordings,
          requireApprovalForGuests: settings.requireApprovalForGuests,
        }
      });
      alert('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const Toggle = ({ enabled, onChange, disabled }) => (
    <button
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`relative w-12 h-6 rounded-full transition-colors ${
        enabled ? 'bg-[#06c]' : 'bg-white/20'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <div
        className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
          enabled ? 'left-7' : 'left-1'
        }`}
      />
    </button>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">Settings</h2>
        <p className="text-white/50 text-sm mt-1">Configure your organization preferences</p>
      </div>

      {/* Organization Info */}
      <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-6">
        <h3 className="text-white font-semibold mb-4">Organization Info</h3>

        {/* Org Name */}
        <div className="space-y-4">
          <div>
            <label className="block text-white/70 text-sm mb-2">Organization Name</label>
            <input
              type="text"
              value={settings.name}
              onChange={(e) => setSettings({ ...settings, name: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#06c]/50"
              placeholder="Your organization name"
            />
          </div>

          {/* Logo Upload */}
          <div>
            <label className="block text-white/70 text-sm mb-2">Organization Logo</label>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#06c] to-purple-600 flex items-center justify-center text-white font-bold text-xl">
                {settings.name?.[0] || 'O'}
              </div>
              <button className="px-4 py-2 bg-white/10 text-white/70 rounded-xl text-sm hover:bg-white/20 transition-colors">
                Upload Logo
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Meeting Defaults */}
      <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-6">
        <h3 className="text-white font-semibold mb-4">Default Meeting Settings</h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div>
              <div className="text-white font-medium text-sm">Enable Waiting Room</div>
              <div className="text-white/50 text-xs mt-0.5">Require approval before guests can join</div>
            </div>
            <Toggle
              enabled={settings.defaultWaitingRoom}
              onChange={(v) => setSettings({ ...settings, defaultWaitingRoom: v })}
            />
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div>
              <div className="text-white font-medium text-sm">Save Chat History</div>
              <div className="text-white/50 text-xs mt-0.5">Automatically save meeting chat messages</div>
            </div>
            <Toggle
              enabled={settings.defaultSaveChatHistory}
              onChange={(v) => setSettings({ ...settings, defaultSaveChatHistory: v })}
            />
          </div>
        </div>
      </div>

      {/* Guest Permissions */}
      <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-6">
        <h3 className="text-white font-semibold mb-4">Guest Permissions</h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div>
              <div className="text-white font-medium text-sm">Allow Guest Recordings</div>
              <div className="text-white/50 text-xs mt-0.5">Let guests download their own recordings</div>
            </div>
            <Toggle
              enabled={settings.allowGuestRecordings}
              onChange={(v) => setSettings({ ...settings, allowGuestRecordings: v })}
            />
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div>
              <div className="text-white font-medium text-sm">Require Approval for Guests</div>
              <div className="text-white/50 text-xs mt-0.5">Hosts must approve guest join requests</div>
            </div>
            <Toggle
              enabled={settings.requireApprovalForGuests}
              onChange={(v) => setSettings({ ...settings, requireApprovalForGuests: v })}
            />
          </div>
        </div>
      </div>

      {/* Roles & Permissions Info */}
      <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-6">
        <h3 className="text-white font-semibold mb-4">Roles & Permissions</h3>

        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-yellow-300 text-lg">👑</span>
              <span className="text-yellow-300 font-medium">Owner</span>
            </div>
            <p className="text-white/60 text-sm">Full access to all settings, billing, and member management</p>
          </div>

          <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-blue-300 text-lg">⚡</span>
              <span className="text-blue-300 font-medium">Admin</span>
            </div>
            <p className="text-white/60 text-sm">Manage members, meetings, and recordings. No billing access.</p>
          </div>

          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-white/70 text-lg">👤</span>
              <span className="text-white/70 font-medium">Member</span>
            </div>
            <p className="text-white/60 text-sm">Host and join meetings. Access own recordings and notes.</p>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      {isOwner && (
        <div className="backdrop-blur-xl bg-gradient-to-br from-red-500/10 to-red-500/5 rounded-2xl border border-red-500/20 p-6">
          <h3 className="text-red-400 font-semibold mb-4">Danger Zone</h3>

          <div className="flex items-center justify-between">
            <div>
              <div className="text-white font-medium text-sm">Delete Organization</div>
              <div className="text-white/50 text-xs mt-0.5">This action cannot be undone</div>
            </div>
            <button className="px-4 py-2 bg-red-500/20 text-red-400 rounded-xl text-sm font-medium hover:bg-red-500/30 transition-colors">
              Delete Org
            </button>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 bg-gradient-to-r from-[#06c] to-purple-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
